/**
 * Composer le produit qu'affiche la fiche de l'Optimiseur de flux.
 *
 * ## Pourquoi ce fichier existe
 *
 * La fiche s'ouvre de deux endroits, et ils ne disposent pas de la même chose :
 *
 * - depuis **l'onglet Analyse**, on a le produit du flux, entier ;
 * - depuis **l'onglet Suggestions**, on a une suggestion — qui n'archive du flux que
 *   `productTitle`, `imageLink`, `productLink` et `productBrand`. Ni la description
 *   d'origine, ni le prix, ni la disponibilité.
 *
 * Le second chemin fabriquait son produit à partir de la seule suggestion, et l'ordre de ses
 * étalements écrasait le peu qu'il avait : la fiche annonçait « Titre absent », sans
 * vignette, « Prix non relevé », sur un produit que la liste montrait complet deux clics plus
 * tôt.
 *
 * ⚠️ **La colonne « Flux source » doit montrer le flux, jamais la suggestion.** C'est toute
 * la difficulté : une suggestion porte des valeurs d'attributs qui *ressemblent* à celles du
 * flux, et les y verser donne une fiche d'apparence correcte où la colonne de gauche répète
 * la colonne de droite. Elles n'y figurent qu'en dernier recours, quand ni l'analyse ni
 * l'archive de la suggestion ne disent rien.
 *
 * ## Pourquoi un module, et pas dans le composant
 *
 * Même raison que `texteAttribut.js` : dans un `.jsx`, rien n'est vérifiable hors ligne — le
 * projet teste au script Node, et Node ne lit pas le JSX. Or la règle qui suit est un ordre
 * de priorité à trois couches, c'est-à-dire précisément le genre de logique qu'on croit juste
 * en la relisant. Celle d'avant l'était.
 */

/**
 * Les attributs d'une suggestion, sous les clés que porte un produit.
 *
 * ⚠️ **Seuls les attributs réellement portés sont posés.** La version d'origine comblait les
 * absents par `''`, et c'était faux deux fois :
 *
 * 1. un produit n'a pas de clé pour un attribut qu'il n'a pas ;
 * 2. l'écran **distingue** « absent de l'analyse » de « présent mais vide » — « Non relevé »
 *    envoie relancer l'analyse, « Non renseigné » envoie corriger le flux source. Combler
 *    transformait le premier en second.
 *
 * Et c'est ce qui effaçait le titre et l'image : dix-neuf clés valant `''` étaient étalées
 * par-dessus celles qu'on venait de poser.
 *
 * @param {Object} attributes  les attributs d'une suggestion, sous leur nom Merchant
 * @param {Array<{nom: string, cleProduit: string}>} attributs  la table servie par le serveur
 */
/**
 * La valeur d'un widget de liste, ramenée à un tableau.
 *
 * ⚠️ **Un widget ne doit jamais faire tomber l'écran entier.** C'est la règle que `texteAttribut`
 * tient déjà pour les valeurs affichées — « aucune valeur ne peut faire tomber l'écran » —, et
 * que les deux widgets éditables ne tenaient pas : ils faisaient `valeur || []`, qui laisse
 * passer un **objet**. Le `.map()` suivant levait un `TypeError`, et c'est la fiche complète qui
 * disparaissait, pas la seule rangée fautive.
 *
 * Les valeurs arrivent de trop d'endroits pour qu'on puisse les garantir toutes : le flux, une
 * suggestion du modèle, une saisie, une analyse enregistrée par une version antérieure du
 * code. Un objet seul est enveloppé — c'est très probablement une rangée unique — et tout le
 * reste rend une liste vide.
 */
export function enTableau(valeur, sousChamps) {
  const brut = Array.isArray(valeur)
    ? valeur
    // Un objet seul est souvent une rangée que personne n'a mise dans un tableau.
    : (valeur && typeof valeur === 'object' ? [valeur] : []);

  if (!Array.isArray(sousChamps) || sousChamps.length === 0) return brut;

  /*
   * ⚠️ **Une rangée qui ne porte aucun des sous-champs attendus n'est pas une rangée.**
   *
   * Relevé sur 1 570 produits du parc : `variantOptionsDeduites` y porte `{axes, membres}` —
   * la *provenance* de la déduction, écrite sous ce nom par une version antérieure, avant que
   * la valeur et la provenance ne soient séparées. Sans ce filtre, l'écran en ferait une
   * rangée aux champs vides, que le marchand croirait avoir saisie.
   *
   * Le filtre vaut au-delà de cet incident : un modèle qui rend un objet d'une autre forme
   * produirait la même rangée fantôme.
   */
  return brut.filter(rangee => (
    rangee && typeof rangee === 'object'
    && sousChamps.some(sc => rangee[sc.cle] !== undefined)
  ));
}

export function produitDepuisAttributs(attributes, attributs) {
  return Object.fromEntries(
    (attributs || [])
      .filter(a => attributes?.[a.nom] !== undefined && attributes[a.nom] !== null)
      .map(a => [a.cleProduit, attributes[a.nom]])
  );
}

/** Les champs du flux qu'une suggestion archive, et leur clé sur un produit. */
const ARCHIVE_DE_LA_SUGGESTION = {
  productTitle: 'title',
  productDescription: 'description',
  imageLink: 'imageLink',
  productLink: 'link',
  productBrand: 'brand'
};

/**
 * Le produit à afficher dans la fiche, pour une suggestion donnée.
 *
 * L'ordre va du moins sûr au plus sûr, et c'est lui qui porte toute la correction :
 *
 * | Rang | Source | Ce que c'est |
 * |---|---|---|
 * | 1 | les attributs **suggérés** | des propositions, pas le flux — dernier recours |
 * | 2 | l'archive de la suggestion | le flux, tel qu'il était à la génération |
 * | 3 | le produit de **l'analyse** | le flux lui-même |
 *
 * ⚠️ `undefined` ne recouvre rien lors d'un étalement : un champ absent d'une couche laisse
 * passer celui d'en dessous. C'est exactement la règle voulue, et c'est pourquoi l'archive
 * est filtrée de ses `undefined` avant d'être étalée — un `title: undefined` explicite
 * effacerait, lui, ce que la couche du dessous aurait posé.
 *
 * @param {Object} suggestion
 * @param {Array} attributs  la table d'attributs servie par le serveur
 * @param {Object} [produitDeLAnalyse]  le produit du flux, s'il est en mémoire
 */
export function produitDeLaFiche(suggestion, attributs, produitDeLAnalyse) {
  const attributes = suggestion?.editedAttributes || suggestion?.suggestedAttributes || {};

  /*
   * ⚠️ `productTitle` est le titre **d'origine**, pas le titre suggéré : `suggestedTitle` et
   * `editedTitle` sont des champs distincts, et ils alimentent la colonne de droite. Les
   * confondre ferait répéter la proposition des deux côtés de la fiche, ce qui se lit comme
   * « le flux portait déjà ça » — et l'on refuserait une suggestion utile.
   */
  const archive = Object.fromEntries(
    Object.entries(ARCHIVE_DE_LA_SUGGESTION)
      .map(([champSuggestion, cleProduit]) => [cleProduit, suggestion?.[champSuggestion]])
      .filter(([, valeur]) => valeur !== undefined && valeur !== null && valeur !== '')
  );

  return {
    ...produitDepuisAttributs(attributes, attributs),
    ...archive,
    ...(produitDeLAnalyse || {}),
    offerId: suggestion?.offerId,
    issues: produitDeLAnalyse?.issues || suggestion?.issues || []
  };
}

/**
 * Les attributs que **cette analyse** ignore, déterminé sur l'ensemble de ses produits.
 *
 * Une analyse antérieure à l'ajout d'un attribut ne le porte sur aucun de ses produits : sa
 * clé est absente partout. C'est la seule façon de le savoir, la projection supprimant les
 * clés sans valeur (`JSON` ne sérialise pas `undefined`).
 *
 * ⚠️ **Le critère est global, et il doit l'être** : une clé présente sur un seul produit
 * prouve que l'analyse connaissait l'attribut, donc que son absence ailleurs est une absence
 * de valeur — pas une perte de projection.
 */
export function attributsInconnusDeLAnalyse(produits, attributs) {
  if (!Array.isArray(produits) || produits.length === 0) return new Set();
  if (!Array.isArray(attributs) || attributs.length === 0) return new Set();

  const clesPresentes = new Set();
  for (const produit of produits) {
    if (produit && typeof produit === 'object') {
      Object.keys(produit).forEach(cle => clesPresentes.add(cle));
    }
  }

  return new Set(
    attributs.filter(a => !clesPresentes.has(a.cleProduit)).map(a => a.nom)
  );
}

/**
 * Les attributs non relevés **pour le produit ouvert**.
 *
 * ## ⚠️ Le défaut que cette séparation corrige
 *
 * Le calcul était en un seul bloc, et accumulait les constats sur **tous** les produits de
 * l'analyse : un attribut constaté quelque part était tenu pour « relevé » partout.
 *
 * Mesuré sur une analyse du parc — 413 produits, aucun ne portant `googleProductCategory` :
 * **21** avaient le constat « Catégorie Google manquante », les **392** autres l'avaient
 * renseignée au moment du scan, et la projection d'alors ne la conservait pas. Les 21
 * suffisaient à faire afficher « Non renseigné » aux 392, c'est-à-dire **« votre flux ne la
 * porte pas »** à propos de produits qui la portent.
 *
 * Les deux mentions n'envoient pas au même endroit, et c'est tout l'enjeu :
 *
 * | Mention | Ce qu'elle dit | Ce qu'on va faire |
 * |---|---|---|
 * | « Non renseigné » | le flux ne porte pas la valeur | corriger le flux source |
 * | « Non relevé par cette analyse » | nous ne l'avons pas conservée | relancer l'analyse |
 *
 * ## La règle
 *
 * « Inconnu de l'analyse » est une propriété de **l'analyse** ; « constaté » est une propriété
 * du **produit**. Un constat sur l'attribut prouve que le contrôle a lu ce produit-là et n'a
 * rien trouvé : l'absence y est donc réelle, et c'est le constat qui parle, pas nous.
 */
export function attributsNonRelevesPour(produit, inconnusDeLAnalyse) {
  if (!inconnusDeLAnalyse || inconnusDeLAnalyse.size === 0) return new Set();

  const constates = new Set(
    (produit?.issues || [])
      .map(constat => constat?.attribute)
      .filter(Boolean)
  );

  return new Set([...inconnusDeLAnalyse].filter(nom => !constates.has(nom)));
}
